// Taking attendance: the rules the drawer and its writer decide with.
//
// **Adapters.** What taking attendance decides — the price the drawer opens
// at, the payment read back, what the form shows, the two money refusals, the
// settlement and the sentence under the status, the Status row's words, the
// History line, the three note audiences and the clamp, the six documents a
// save writes and the three a clear writes (#765), the tombstone, the
// cancellation notice's ticks and its two "already told" checks, and the
// student profile's attendance tab — is `core/src/lesson.rs`, so an Android or
// iOS drawer opens at the same price, refuses the same figures, says the same
// sentence and writes the same documents. The arguments for each rule are at
// the call sites in `useAttendanceForm.js`, `useAttendance.jsx` and the
// drawer's tabs. `core/src/lesson_vectors.json` holds the recorded answers,
// and `test/core/lesson.wasm.test.js` replays them through these adapters.
//
// What stays here is the coercion into the core's records — a stored number
// read with `Number()`, a flag with `=== true` where the rule said so — and
// the tables components read at module scope (`NOTE_AUDIENCES`, the notice
// ticks, the tab's window), pinned to the core's by
// `test/domain/lessonCore.test.js`. A student with `familyId: null` is
// refused before anything is queued (`no_family`, argued at `NO_FAMILY` in
// `core/src/lesson.rs`).
//
// The money figures a sentence quotes are handed in already formatted,
// because formatting money is the studio's locale (`useMoneyFormat`) and not
// a rule.
import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';
import { eventOccurrenceInput } from './eventSeries.js';
import { tombstoneOf } from './tombstone.js';

// The payment's description when the teacher typed no note.
export const PAID_AT_LESSON = 'Paid at lesson';

const str = (v) => (v == null ? null : String(v));
const num = (v) => (v == null ? null : Number(v));

// The core's `EventOccurrence` for these rules: calendar's, with
// `requiresMakeupCredit` read the way every attendance rule reads it — `true`
// and nothing else.
function lessonOccurrenceInput(o) {
  if (!o) return null;
  return { ...eventOccurrenceInput(o), requiresMakeupCredit: o.requiresMakeupCredit === true };
}

// The core's `LessonStudent`.
function lessonStudentInput(s) {
  if (!s) return null;
  return {
    id: str(s.id),
    familyId: str(s.familyId),
    pricingMode: str(s.pricingMode),
    rateMinor: num(s.rateMinor),
    durationMin: num(s.durationMin),
  };
}

// ── The price the form opens at ───────────────────────────────────────────

// What this lesson costs before any manual override (`?? 0` is argued at the
// call site in useAttendanceForm.js, #322).
export function attendanceComputedMinor(occurrence, student) {
  return coreSync().attendanceComputedMinor(lessonOccurrenceInput(occurrence), lessonStudentInput(student));
}

// The payment taken at an earlier save, read back off the ledger — `null`
// when there is none, it is retracted, or it moved nothing.
export function attendancePaymentReadBack(entry) {
  if (!entry) return null;
  const back = coreSync().attendancePaymentReadBack({
    id: String(entry.id ?? ''),
    amountMinor: Number(entry.amountMinor),
    description: str(entry.description),
    ...tombstoneOf(entry),
  });
  return back ? { id: back.id, amountMinor: back.amountMinor, note: back.note } : null;
}

// ── What the form shows ───────────────────────────────────────────────────

export function attendanceDisclosure({ status, requiresMakeupCredit = false, paidAtLesson = false } = {}) {
  const d = coreSync().attendanceDisclosure(str(status), requiresMakeupCredit === true, Boolean(paidAtLesson));
  return {
    cancelling: d.cancelling,
    lessonBills: d.lessonBills,
    showPrice: d.showPrice,
    paymentApplies: d.paymentApplies,
  };
}

export function attendanceFieldErrors({
  priceText = '', paidText = '', showPrice = false, paymentApplies = false,
} = {}, currency = 'USD') {
  const e = coreSync().attendanceFieldErrors(
    String(priceText ?? ''), String(paidText ?? ''), Boolean(showPrice), Boolean(paymentApplies), currency,
  );
  return { price: e.priceMessage ?? null, paid: e.paidMessage ?? null };
}

export function attendanceUsesPackage(remaining, pricingRule) {
  return coreSync().attendanceUsesPackage(num(remaining), str(pricingRule));
}

export function attendanceSettlement({ priceText = '', paidText = '', computedMinor = 0 } = {}, currency = 'USD') {
  const s = coreSync().attendanceSettlement(
    String(priceText ?? ''), String(paidText ?? ''), Number(computedMinor) || 0, currency,
  );
  return {
    chargeMinor: s.chargeMinor, paidMinor: s.paidMinor, owingMinor: s.owingMinor, differenceMinor: s.differenceMinor,
  };
}

// The sentence under the status. `money` holds the settlement's three
// figures as the studio's locale writes them, and `decimal`, the Region's
// decimal separator the make-up credits left are written with (`0,75 left`
// in Germany, #549) — required, as `formatCredits`'s is.
export function attendanceConsequence({
  status, savedStatus = null, requiresMakeupCredit = false, usesPackage = false,
  remaining = 0, availableMin = 0, lessonDurationMin, occurrenceDurationMin,
  paymentApplies = false, owingMinor = 0,
} = {}, money = {}) {
  return coreSync().attendanceConsequence({
    status: str(status),
    savedStatus: str(savedStatus),
    requiresMakeupCredit: requiresMakeupCredit === true,
    usesPackage: Boolean(usesPackage),
    remaining: num(remaining),
    availableMin: num(availableMin),
    lessonDurationMin: num(lessonDurationMin),
    occurrenceDurationMin: num(occurrenceDurationMin),
    paymentApplies: Boolean(paymentApplies),
    owingMinor: Number(owingMinor) || 0,
  }, String(money.charge ?? ''), String(money.paid ?? ''), String(money.difference ?? ''), money.decimal);
}

// One row of the Status select (AttendanceTab.jsx).
export function attendanceOptionLabel(st) {
  return coreSync().attendanceOptionLabel(
    String(st.label), Boolean(st.billable), Boolean(st.issuesMakeupCredit), Boolean(st.consumesMakeupCredit),
  );
}

// The History tab's one line (HistoryTab.jsx has the argument for each).
export function attendanceHistoryText(record, viewerUid = null) {
  return coreSync().attendanceHistoryText(str(record?.updatedBy), Boolean(record?.updatedAt), str(viewerUid));
}

// ── The lesson notes ──────────────────────────────────────────────────────

// The three bodies, one per audience, for a component that reads them at
// module scope; pinned to `noteAudiences()` in the core.
export const NOTE_AUDIENCES = Object.freeze([
  Object.freeze({ id: 'student', label: 'Student', visibility: 'Visible to this student and their parent(s)' }),
  Object.freeze({ id: 'parent', label: 'Parent', visibility: "Visible to this student's parent(s) only" }),
  Object.freeze({ id: 'private', label: 'Private', visibility: 'Visible only to you' }),
]);

// The audience shown, falling back to the first — this table's own row, so a
// caller comparing by identity still can.
export function noteAudience(id) {
  const { id: found } = coreSync().noteAudience(str(id));
  return NOTE_AUDIENCES.find((a) => a.id === found);
}

export function notePlaceholder(id) {
  return coreSync().notePlaceholder(str(id));
}

export function notePreviewEmptyText(label) {
  return coreSync().notePreviewEmptyText(String(label));
}

// A body as `setNote` stores it: clamped to the bound. A cut that falls
// between the halves of a surrogate pair drops the lone half, where `slice`
// would keep it (the core says why).
export function clampNote(value) {
  return coreSync().clampNote(String(value ?? ''));
}

// ── What a save writes ────────────────────────────────────────────────────

// The charge's line: `4:00 PM (45 min.) - Lesson`, leaving out whichever part
// the occurrence does not have.
//
// `timeText` is the start on the studio's Region's clock (`formatTime`, #618):
// the line is stored on the charge, so it is written the way the studio reads
// a time everywhere else — `4:00 PM` in the United States, as MyMusicStaff's
// family ledger writes it, and `16:00` in Germany. Left off, the stored `HH:MM`.
export function attendanceDescription(occurrence, timeText = null) {
  return coreSync().attendanceDescription(
    eventOccurrenceInput(occurrence),
    timeText ? String(timeText) : null,
  );
}

// The core's `AttendanceSave`, from `setAttendance`'s input.
// `resourceIds` (#424): the lesson's Linked Online Resources, or null to leave
// the stored list alone — which is what a caller that does not know about
// links passes, so it cannot unlink anything by saving.
function attendanceSaveInput({
  status, priceOverrideMinor = null, packageRemaining = 0, notes = null, payment = null, description = null,
  resourceIds = null,
}) {
  return {
    status: str(status),
    priceOverrideMinor: num(priceOverrideMinor),
    packageRemaining: num(packageRemaining),
    writeNotes: Boolean(notes),
    studentNote: str(notes?.student || ''),
    parentNote: str(notes?.parent || ''),
    privateNote: str(notes?.private || ''),
    paid: Boolean(payment?.paid),
    paidMinor: num(payment?.amountMinor),
    paymentNote: str(payment?.note),
    resourceIds: Array.isArray(resourceIds) ? resourceIds.map(String) : null,
    description: str(description),
  };
}

// A save the core refuses, as the error `setAttendance` throws: the sentence,
// with the core's code beside it.
export class AttendanceRefused extends Error {
  constructor({ code, message }) {
    super(message);
    this.name = 'AttendanceRefused';
    this.code = code;
  }
}

// The documents one attendance decision writes, in batch order, each a merging
// set: `{ collection, id, data }`. Throws `AttendanceRefused` for a save that
// cannot be written — no business date, an amount that is not whole minor
// units, or no family — before anything is queued.
export function attendanceDocuments({
  occurrence, student, status,
  priceOverrideMinor = null, packageRemaining = 0,
  notes = null, payment = null, resourceIds = null, uid,
}, serverTimestamp) {
  const core = coreSync();
  const o = lessonOccurrenceInput(occurrence);
  const s = lessonStudentInput(student);
  const save = attendanceSaveInput({
    status, priceOverrideMinor, packageRemaining, notes, payment, resourceIds,
    description: occurrence?.description,
  });
  const refusal = core.attendanceRefusal(o, s, save, String(uid));
  if (refusal) throw new AttendanceRefused(refusal);
  return core.attendanceWrites(o, s, save, String(uid)).map(({ part, collection, id }) => ({
    collection,
    id,
    data: documentFromRows(core.attendanceDocument(part, o, s, save, String(uid)), serverTimestamp),
  }));
}

// The documents clearing one student's attendance back to unrecorded writes,
// in batch order, each a merging set: `{ collection, id, data }` — the
// record's status and both make-up entries retracted, and nothing else
// (`attendance_clear_writes` in core/src/lesson.rs says what is left alone;
// see #767). `core/src/attendance_clear_vectors.json` holds the recorded
// documents (#765). Throws `AttendanceRefused` for an occurrence with no
// business date.
export function attendanceClearDocuments({ occurrence, student, uid }, serverTimestamp) {
  const core = coreSync();
  const o = lessonOccurrenceInput(occurrence);
  const s = lessonStudentInput(student);
  const refusal = core.attendanceClearRefusal(o, s, String(uid));
  if (refusal) throw new AttendanceRefused(refusal);
  return core.attendanceClearWrites(o, s, String(uid)).map(({ part, collection, id }) => ({
    collection,
    id,
    data: documentFromRows(core.attendanceClearDocument(part, o, s, String(uid)), serverTimestamp),
  }));
}

// Both halves of a tombstone, as a merging set writes them onto a document
// that exists (`voidAttendanceEffects`; firestore.rules' `tombstoneAgrees`).
export function tombstoneDocument(serverTimestamp) {
  return documentFromRows(coreSync().tombstoneDocument(), serverTimestamp);
}

// ── Telling the family the lesson is off (#187) ───────────────────────────

// One channel's ticks, closed and opened; pinned to the core's toggle.
export const CLOSED_NOTICE = Object.freeze({ open: false, students: false, parents: false });
export const OPEN_NOTICE = Object.freeze({ open: true, students: true, parents: true });

const noticeInput = (c) => (c
  ? { open: Boolean(c.open), students: Boolean(c.students), parents: Boolean(c.parents) }
  : null);

export function toggledNoticeChannel(current) {
  const n = coreSync().toggledNoticeChannel(noticeInput(current));
  return { open: n.open, students: n.students, parents: n.parents };
}

export function noticeChannelBox(current = {}) {
  const b = coreSync().noticeChannelBox(noticeInput(current));
  return { checked: b.checked, indeterminate: b.indeterminate };
}

// What the queue is asked for: per channel, the ticked audiences or null.
export function chosenNotices(notices = {}, note = '') {
  const pick = (channel) => {
    const c = coreSync().chosenNotice(noticeInput(notices?.[channel]));
    return c ? { students: c.students, parents: c.parents } : null;
  };
  return { email: pick('email'), sms: pick('sms'), note };
}

const messageRows = (messages = []) => messages.map((m) => ({ id: String(m?.id ?? ''), status: str(m?.status) }));

// The channels a reminder for this lesson has already gone out on, email first.
export function remindedChannels(messages = [], occurrence, student) {
  if (!occurrence || !student?.familyId) return [];
  return coreSync().remindedChannels(
    messageRows(messages), String(occurrence.id), str(occurrence.date), str(student.familyId),
  );
}

// A notice already asked for on this lesson-and-student that has not failed.
export function noticeAlreadyQueued(messages = [], occurrence, student) {
  if (!occurrence || !student) return false;
  return coreSync().noticeAlreadyQueued(
    messageRows(messages), String(occurrence.id), String(student.id), str(student.familyId),
  );
}

// ── A student's attendance tab ────────────────────────────────────────────

// How far back and forward the tab folds, in days; pinned to the core's window.
export const ATTENDANCE_DAYS_BACK = 180;
export const ATTENDANCE_DAYS_FORWARD = 90;

export function attendanceWindow(today) {
  const { from, to } = coreSync().attendanceWindow(today);
  return [from, to];
}

// This student's occurrences with their status stamped on, newest first: by
// date, then start time, then id, each descending (`newest_first_rows` in
// core/src/lesson.rs), so two lessons on one day list the later first
// whatever order the fold produced them in.
export function studentAttendanceHistory(occurrences = [], studentId, statusFor) {
  const rows = occurrences
    .filter((o) => o.studentIds.includes(studentId))
    .map((o) => ({ ...o, status: statusFor(o.id, studentId) }));
  return coreSync().newestFirstRows(rows.map((o) => ({
    date: String(o.date ?? ''), startTime: str(o.startTime), id: String(o.id ?? ''),
  }))).map((i) => rows[i]);
}

export function studentAttendanceStats(history = [], today) {
  const s = coreSync().attendanceStats(
    history.map((h) => ({ date: String(h.date ?? ''), status: String(h.status ?? '') })), today,
  );
  return { present: s.present, absent: s.absent, unrecorded: s.unrecorded, total: s.total, rate: s.rate };
}

export function attendanceBillingLabel(status, requiresMakeupCredit = false) {
  return coreSync().attendanceBillingLabel(str(status), requiresMakeupCredit === true);
}
